import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Modal, Form, Alert } from "react-bootstrap";
import { Button } from "./Controls/Button";
import { createApiService } from "./shared/apiConfig";
import { ReservationApi } from "./shared/api/axios-client";
import { toast } from "react-toastify";
import { CheckCircleFill, TicketPerforatedFill } from "react-bootstrap-icons";
import { showMoney, cents } from "./Data/Money";

export function ValidationRedeemPage() {
    const { facilityId, validationRedeemId } = useParams();
    const nav = useNavigate();

    const [showIntroModal, setShowIntroModal] = useState(true);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [confirmationCode, setConfirmationCode] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [validationResponse, setValidationResponse] = useState<{
        discount: number | null;
        newPrice: number | null;
    } | null>(null);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setErrorMessage("");
        setIsSubmitting(true);

        try {
            const reservationApi = createApiService(ReservationApi);
            const response = await reservationApi.validateReservation({
                serverRequestsReservationValidateRequest: {
                    validationRateId: validationRedeemId!,
                    confirmationNum: confirmationCode,
                    facilityId: facilityId ? Number(facilityId) : undefined,
                }
            });

            if (response.data.wasSuccessful) {
                setValidationResponse({
                    discount: response.data.discount ?? null,
                    newPrice: response.data.newPrice ?? null
                });
                setShowSuccessModal(true);
            } else {
                setErrorMessage(response.data.message || "Unable to validate your reservation. Please check your confirmation code and try again.");
            }
        } catch (error: any) {
            const serverMessage = error?.response?.data?.message;
            setErrorMessage(serverMessage || "Error processing your validation. Please try again.");
            toast.error(serverMessage || "Error processing validation.");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            <Modal centered show={showIntroModal} onHide={() => setShowIntroModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title><TicketPerforatedFill className="me-1 icon-shift-up" /> Redeem Validation</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="mb-3">
                        <p className="mb-2">
                            Enter your confirmation code to apply your parking validation discount.
                        </p>
                        <div className="bg-light p-3 rounded">
                            <small className="text-muted">
                                <strong>Before you begin:</strong>
                                <ul className="mb-0 mt-1">
                                    <li>Your confirmation code can be found in the email receipt you received for your parking session</li>
                                    <li>Once validated, you'll receive an updated email with your new payment amount</li>
                                </ul>
                            </small>
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button className="btn-main" onClick={() => setShowIntroModal(false)}>
                        Continue
                    </Button>
                </Modal.Footer>
            </Modal>

            <div className="container mt-4">
                <h3 className="mb-4 text-center">Redeem Parking Validation</h3>
              
                <div className="border rounded-3 p-4 shadow" style={{ maxWidth: "600px", margin: "0 auto" }}>
                    {errorMessage && (
                        <Alert variant="danger" dismissible onClose={() => setErrorMessage("")}>
                            {errorMessage}
                        </Alert>
                    )}

                    <Form onSubmit={handleSubmit}>
                        <Form.Group className="mb-3">
                            <Form.Label>Confirmation Code</Form.Label>
                            <Form.Control
                                type="text"
                                name="confirmationCode"
                                value={confirmationCode}
                                onChange={(e) => setConfirmationCode(e.target.value)}
                                placeholder="Enter your confirmation code"
                                required
                                disabled={isSubmitting}
                                autoFocus
                            />
                            <Form.Text className="text-muted">
                                You can find this code in your parking session email receipt.
                            </Form.Text>
                        </Form.Group>

                        <Button 
                            className="btn-main w-100" 
                            type="submit" 
                            disabled={isSubmitting || !confirmationCode.trim()}
                        >
                            {isSubmitting ? "Processing..." : "Apply Validation"}
                        </Button>
                    </Form>
                </div>
            </div>

            <Modal centered show={showSuccessModal} onHide={() => nav("/")}>
                <Modal.Header closeButton>
                    <Modal.Title><CheckCircleFill className="me-1 icon-shift-up text-success" /> Validation Applied!</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="mb-3">
                        <p className="mb-3"><strong>Your parking validation has been successfully applied.</strong></p>
                        
                        {validationResponse && validationResponse.discount !== null && validationResponse.newPrice !== null && (
                            <div className="border rounded overflow-hidden">
                                <div className="px-3 py-2 d-flex justify-content-between border-bottom">
                                    <span>Original Price</span>
                                    <span className="fw-medium">{showMoney(cents(validationResponse.newPrice + validationResponse.discount))}</span>
                                </div>
                                <div className="px-3 py-2 d-flex justify-content-between border-bottom fw-bold">
                                    <span>Validation Discount</span>
                                    <span className="fw-bold">-{showMoney(cents(validationResponse.discount))}</span>
                                </div>
                                <div className="px-3 py-3 d-flex justify-content-between fw-bold" 
                                     style={{ background: 'linear-gradient(135deg, rgb(85, 85, 85) 0%, rgb(100, 100, 100) 100%)' }}>
                                    <span className="text-white">New Total</span>
                                    <span className="text-white">{showMoney(cents(validationResponse.newPrice))}</span>
                                </div>
                            </div>
                        )}
                        
                        <div className="mt-3 text-muted">
                            <small>
                                You will receive an email confirmation shortly with the updated payment amount..
                            </small>
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button className="btn-main" onClick={() => nav("/")}>Done</Button>
                </Modal.Footer>
            </Modal>
        </>
    );
}

